import {Breakpoints} from '@angular/cdk/layout';
import {Component, Inject, Input, OnDestroy} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, EMPTY, firstValueFrom, from} from 'rxjs';
import {filter, first, map} from 'rxjs/operators';

import {ClassName} from '@sim/role/models/role';
import {RoleService} from '@sim/role/services/role.service';
import {
  BaseTableComponent,
  TableContextMenu,
  TableField,
  UiTableComponent,
} from '@ui/ui.module';

import {CardsApiService, CardType, filePath, SourceBibApiService, SwalService} from '@atmi/core';
import {CardD, CardMI, cardsOptions, CommonCard, SourceBibliographic} from '@atmi/core/models';
import {CardsSourceBibDialogComponent} from '../cards-source-bib-dialog/cards-source-bib-dialog.component';
import {CardF} from '@src/app/core/models/cards/card-f';
import {CardOA} from '@src/app/core/models/cards/card-oa';
import {CardS} from '@src/app/core/models/cards/card-s';

type SourceBibliographicConcrete = SourceBibliographic & { id: string };

@Component({
  selector: 'app-bib-cards',
  styleUrls: ['./bib-cards.component.scss'],
  templateUrl: './bib-cards.component.html',
})
export class BibCardsComponent
  extends BaseTableComponent<CommonCard>
  implements UiTableComponent<CommonCard>, OnDestroy {

  private readonly sourceBib$ = new BehaviorSubject<SourceBibliographic | null>(null);

  @Input() set sourceBib(sourceBib: SourceBibliographic | null) {
    if (sourceBib) {
      this.sourceBib$.next(sourceBib);
    }
  }

  get sourceBib() {
    return this.sourceBib$.value;
  }

  readonly fields: TableField<CommonCard>[] = [
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Tipo scheda'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'TSK',
    },
    {
      header: this.translate.instant('Codice unico'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'codiceUnico',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      key: 'SGT.SGTT',
    },
    {
      header: this.translate.instant('Fornitore'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'fornitore',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      template: 'date-time',
    },
  ];

  readonly pager$ = this.sourceBib$.pipe(
    filter((sourceBib): sourceBib is SourceBibliographicConcrete => !!sourceBib),
    map((sourceBib) => ((page?: number, limit?: number) =>
      this.sourceBibApi.getCards(sourceBib.id, {page, limit}))),
  );

  readonly contextMenus: Array<TableContextMenu<CommonCard>> = [];

  readonly cardOptions = cardsOptions;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly dialog: MatDialog,
    private readonly roleService: RoleService,
    private readonly sourceBibApi: SourceBibApiService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
  ) {
    super(translate, router, route, swalService);

    const hasCapabilityToUpdate = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => this.roleService.hasCapability(className, ['update'])
    );

    if (hasCapabilityToUpdate) {
      this.contextMenus.push({
        action: (item) => this.router.navigate([`cards/${item.TSK.toLowerCase()}/manager`, item.id]),
        label: this.translate.instant('Modifica'),
      });
    } else {
      this.contextMenus.push({
        action: (item) => this.router.navigate([`cards/${item.TSK.toLowerCase()}/manager`, item.id]),
        label: this.translate.instant('Visualizza'),
      });
    }

    const hasCapabilityToDelete = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => this.roleService.hasCapability(className, ['delete'])
    );

    if (hasCapabilityToDelete) {
      this.contextMenus.push({
        action: (item) => item.id
          ? this.deleteHandler(item.id, item, {text: 'Sei sicuro di voler procede?', title: 'Scollega bene'})
          : () => undefined,
        label: this.translate.instant('Scollega bene'),
      });
    }

  }

  ngOnDestroy() {
    this.sourceBib$.complete();
  }

  deleteConcreteHandler(_: string, card: CommonCard) {
    const cardToUpdate: CommonCard = {
      ...card,
      fontiBib: [...card.fontiBib.filter((fb) => fb.docBib !== this.sourceBib?.id)],
    };

    switch (card.TSK.toLowerCase()) {
      case 'oa':
        return this.cardsApi.putOA(cardToUpdate as CardOA);
      case 'd':
        return this.cardsApi.putD(cardToUpdate as CardD, 'd');
      case 'mi':
        return this.cardsApi.put(cardToUpdate as CardMI, 'mi');
      case 'f':
        return this.cardsApi.putF(cardToUpdate as CardF, 'f');
      case 's':
        return this.cardsApi.putS(cardToUpdate as CardS, 's');
      default:
        return this.cardsApi.putOA(cardToUpdate as CardOA);
    }
  }

  // tslint:disable-next-line:variable-name
  deleteSelectedConcreteHandler(_ids: string[]) {
    return from(Promise.all([]));
  }

  async bindCardHandler(type: CardType) {

    if (!this.sourceBib) {
      return;
    }

    const ref = this.dialog.open(CardsSourceBibDialogComponent,
      {data: {type, docBib: this.sourceBib.id}, width: '1000px'});
    const result = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (result) {
      this.reload$.next(true);
    }
  }
}
